import { useCallback, useEffect, useRef, useState } from "react";
import type { CockpitTileAnfrage } from "@kundeninfo/api-client";
import type { CockpitClient } from "./cockpitClient.js";
import type { TileState } from "./CockpitTile.js";

/** Entscheid 669: Jede Änderung der Abfrage (Zeitraum, Vergleich, Unternehmen, neuer Tag) lädt die Kacheln erst nach so viel Ruhe neu; jeder weitere Wechsel stellt die Uhr zurück. Ein Neuladen bei gleicher Abfrage geht sofort. */
export const RELOAD_QUIET_MS = 300;
/** Die Abfrage eines verworfenen Laufs: Sie gleicht keiner echten, darum lädt die Ruhe die Kachel sicher nach, auch wenn die Wahl zurückkehrt. */
const DISCARDED = "";

/** Eine Kachel ohne Antwort. */
export const TILE_LOADING: TileState = { status: "laedt", data: null, stale: false };

/** Der jüngste Abruf einer Kachel: sein Abbruch und die Abfrage, für die er lief. */
type Run = { controller: AbortController; queryId: string };

/** Wartet auf eine Antwort: Was schon zu sehen war, bleibt abgeblendet stehen, sonst „lädt“. */
const pending = (old: TileState | undefined): TileState => (old?.data == null ? TILE_LOADING : { status: "laedt", data: old.data, stale: true });

/**
 * LADEN JE KACHEL (Spec „Laden, Fehler, Neuladen“). Jede Kachel lädt für sich, ein Fehler bleibt bei ihr. Jeder neue Lauf bricht den
 * vorigen derselben Kachel ab; darum entscheidet genau eine Prüfung, ob eine Antwort noch gilt: `signal.aborted` (K34). Sie deckt die
 * überholte Antwort, den Fehler des Abbruchs (der Klient wirft `NETZWERKFEHLER` oder `UNBEKANNT`, der Code zählt nicht), die ausgeblendete
 * Kachel und das Aushängen. Abfrage und Schlüssel zählen nach Inhalt, nicht nach Objekt: Neue Schlüssel laden allein und sofort, eine neue
 * Abfrage alle — erst nach `RELOAD_QUIET_MS` Ruhe (Entscheid 669). Der laufende Abruf ist beim Wechsel sofort verworfen (dasselbe `abort`), die
 * Kachel bleibt mit ihrem alten Inhalt abgeblendet eingehängt: Nichts springt, die Rollposition bleibt.
 */
export function useCockpitTiles(client: CockpitClient, keys: readonly string[], query: CockpitTileAnfrage):
  { states: Readonly<Record<string, TileState>>; retry: (key: string) => void; reloadAll: () => void } {
  const [states, setStates] = useState<Readonly<Record<string, TileState>>>({});
  const runs = useRef(new Map<string, Run>());
  const queryId = JSON.stringify(query);
  const latest = useRef({ client, keys, query, queryId });
  latest.current = { client, keys, query, queryId };
  const quiet = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** Die Abfrage, auf die die Ruhe zuletzt gestellt wurde: Nur ihr Wechsel stellt die Uhr zurück, ein neuer Schlüssel nicht. */
  const settled = useRef(queryId);

  /** `background` (Fokus, Mitternacht, dieselbe Abfrage): Eine gefüllte Kachel bleibt auf „ok“ stehen und dimmt nicht. Scheitert der Abruf, behält sie ihren Stand; der nächste Fokus versucht es wieder. */
  const load = useCallback((key: string, background = false): void => {
    const { client: c, query: q, queryId: id } = latest.current;
    runs.current.get(key)?.controller.abort();
    const controller = new AbortController();
    runs.current.set(key, { controller, queryId: id });
    const settle = (state: (old: TileState | undefined) => TileState): void => {
      if (!controller.signal.aborted) setStates((s) => ({ ...s, [key]: state(s[key]) }));
    };
    setStates((s) => (background && s[key]?.status === "ok" ? s : { ...s, [key]: pending(s[key]) }));
    c.getCockpitTile(key, q, controller.signal).then(
      (data) => { settle(() => ({ status: "ok", data, stale: false })); },
      () => { settle((old) => (old?.status === "ok" ? old : { status: "fehler", data: null, stale: false })); },
    );
  }, []);

  const reloadAll = useCallback((): void => { latest.current.keys.forEach((key) => { load(key, true); }); }, [load]);

  const keysId = JSON.stringify(keys);
  useEffect(() => {
    const wanted = latest.current.keys;
    const gone = [...runs.current.keys()].filter((key) => !wanted.includes(key));
    for (const key of gone) {
      runs.current.get(key)!.controller.abort();
      runs.current.delete(key);
    }
    if (gone.length > 0) setStates((s) => Object.fromEntries(Object.entries(s).filter(([key]) => !gone.includes(key))));
    // Nie geladen (erster Abruf, neu eingeblendet): sofort. Für eine andere Abfrage geladen: verwerfen, abblenden, nach der Ruhe laden.
    const outdated: string[] = [];
    for (const key of wanted) {
      const run = runs.current.get(key);
      if (run === undefined) load(key);
      else if (run.queryId !== queryId) outdated.push(key);
    }
    const queryChanged = settled.current !== queryId;
    settled.current = queryId;
    if (outdated.length === 0 || !queryChanged) return;
    const running = outdated.filter((key) => runs.current.get(key)!.queryId !== DISCARDED);
    for (const key of running) {
      const run = runs.current.get(key)!;
      run.controller.abort();
      runs.current.set(key, { ...run, queryId: DISCARDED });
    }
    if (running.length > 0) setStates((s) => ({ ...s, ...Object.fromEntries(running.map((key) => [key, pending(s[key])])) }));
    clearTimeout(quiet.current);
    quiet.current = setTimeout(() => {
      quiet.current = undefined;
      const { keys: now, queryId: id } = latest.current;
      for (const key of now) if (runs.current.get(key)?.queryId !== id) load(key);
    }, RELOAD_QUIET_MS);
  }, [keysId, queryId, load]);

  useEffect(() => () => {
    clearTimeout(quiet.current);
    for (const run of runs.current.values()) run.controller.abort();
    runs.current.clear();
  }, []);

  return { states, retry: load, reloadAll };
}
